import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Alert, AlertDescription } from '@cotera/client/v0/components/ui/alert';
import {
  Tool,
  ToolHeader,
  ToolContent,
} from '@cotera/client/v0/components/ai-elements/tool';
import { CodeBlock } from '@cotera/client/v0/components/ai-elements/code-block';
import { CodeBlockCopyButton } from '@cotera/client/v0/components/ui/code-block';
import { useAction } from '@cotera/client/v0/actions/framework';
import { RunToolAction } from '../chat/actions/coco-run-tool.action';
import type {
  HijackerWizardContext,
  InputHijackerProps,
} from '@cotera/client/v0/chat/input-hijackers';
import { translateToolName } from './translate-tool-name';
import { cn } from '@cotera/client/v0/lib/utils';
import { Icon, toast } from '@cotera/client/app/components/ui';
import {
  useAnalytics,
  useCocoConversationId,
  useChatSurface,
  CocoEvent,
} from '@cotera/client/app/etc';
import { useSuspensfulToolInfo } from '../../hooks/use-tools-info';
import { useAgentDisplayNameResolver } from '../../hooks/use-agents-display-names';

/**
 * ToolApprovalHijacker - Renders tool approval UI inside the chat input area.
 *
 * When a tool requires approval, this component hijacks the input box to show:
 * - The tool name and input parameters
 * - Approve/Reject buttons
 *
 * The user can also type in the main input box to provide an alternative,
 * which will reject the tool and submit their message instead.
 *
 * This prevents tool approvals from cluttering the message thread and gives
 * users a focused interface for handling tool approvals.
 */
export const ToolApprovalHijacker: React.FC<
  InputHijackerProps & { wizard?: HijackerWizardContext }
> = ({ part, embedded = true, wizard, conversationId }) => {
  const toolInfo = useSuspensfulToolInfo(part.toolKey().toString());
  const getAgentDisplayName = useAgentDisplayNameResolver();
  const runToolAction = useAction(RunToolAction);
  const analytics = useAnalytics();
  const cocoConversationId = useCocoConversationId();
  const surface = useChatSurface() ?? 'scratch';

  const [isSubmitting, setIsSubmitting] = useState(false);

  const toolName =
    getAgentDisplayName(part.toolKey().toString()) ??
    toolInfo?.name ??
    part.toolKey().displayName();

  const toolCallId = part.toolCallId;
  const input = part.input;

  const handleApprove = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    const result = await runToolAction.execute({
      toolCallId,
      action: 'approve',
      conversationId,
    });

    setIsSubmitting(false);

    if (result.isErr()) {
      toast.error('Failed to approve tool');
    }
  }, [runToolAction, toolCallId, isSubmitting, conversationId]);

  const handleReject = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    const result = await runToolAction.execute({
      toolCallId,
      action: 'deny',
      reason: 'User denied tool execution',
      conversationId,
    });

    setIsSubmitting(false);

    if (result.isErr()) {
      toast.error('Failed to reject tool');
    }
  }, [runToolAction, toolCallId, isSubmitting, conversationId]);

  // Memoize the buttons to prevent infinite loops with wizard registration
  const skipButton = useMemo(
    () => (
      <Button
        size="sm"
        variant="outline"
        onClick={() => void handleReject()}
        disabled={isSubmitting}
      >
        <Icon icon="x-mark" />
        Reject
      </Button>
    ),
    [handleReject, isSubmitting]
  );

  const submitButton = useMemo(
    () => (
      <Button
        size="sm"
        variant="default"
        onClick={() => void handleApprove()}
        disabled={isSubmitting}
      >
        <Icon icon="check" />
        Approve
      </Button>
    ),
    [handleApprove, isSubmitting]
  );

  useEffect(() => {
    if (!wizard) {
      return;
    }

    wizard.registerSkipButton(skipButton);
    wizard.registerSubmitButton(submitButton);

    return () => {
      // Unregister on cleanup
      wizard.registerSkipButton(null);
      wizard.registerSubmitButton(null);
    };
  }, [wizard, skipButton, submitButton]);

  return (
    <div
      className={cn('flex min-h-0 w-full min-w-0 flex-col', embedded && 'px-1')}
    >
      <Tool
        defaultOpen
        className="mb-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      >
        <ToolHeader
          state="input-available"
          title={translateToolName(toolName, 'waiting')}
          type={part.toolPart.type}
        />
        <ToolContent className="min-h-0 flex-1 overflow-hidden p-0 data-[state=open]:flex data-[state=open]:min-h-0 data-[state=open]:flex-1 data-[state=open]:flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="space-y-2 p-4">
              <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                Parameters
              </h4>
              <div className="rounded-md bg-muted/50">
                <CodeBlock
                  code={JSON.stringify(input, null, 2)}
                  language="json"
                  className="max-h-none"
                >
                  <CodeBlockCopyButton
                    onCopy={() => {
                      if (cocoConversationId !== null) {
                        analytics.track(CocoEvent.CodeBlockCopied, {
                          conversationId: cocoConversationId,
                          surface,
                          language: 'json',
                          source: 'tool-approval',
                        });
                      }
                    }}
                  />
                </CodeBlock>
              </div>
            </div>
            <div className="px-4 pb-4">
              <Alert className="flex flex-col gap-3 bg-card/60 border-border">
                <AlertDescription className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {toolName}
                  </span>{' '}
                  needs your approval to execute.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </ToolContent>
      </Tool>
    </div>
  );
};
